"use client"

import { useCallback, useEffect, useRef, useState } from "react"

// The API ships under two names and has no lib.dom types, so the shape the
// hook actually uses is declared here rather than pulled from the DOM lib.
interface SpeechRecognitionAlternativeLike {
	transcript: string
}
interface SpeechRecognitionResultLike {
	isFinal: boolean
	0: SpeechRecognitionAlternativeLike
}
interface SpeechRecognitionEventLike {
	resultIndex: number
	results: {
		length: number
		[index: number]: SpeechRecognitionResultLike
	}
}
interface SpeechRecognitionErrorEventLike {
	error: string
}
interface SpeechRecognitionLike {
	lang: string
	continuous: boolean
	interimResults: boolean
	start: () => void
	stop: () => void
	abort: () => void
	onresult: ((event: SpeechRecognitionEventLike) => void) | null
	onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
	onend: (() => void) | null
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function recognitionCtor(): SpeechRecognitionCtor | null {
	if (typeof window === "undefined") return null
	const w = window as unknown as {
		SpeechRecognition?: SpeechRecognitionCtor
		webkitSpeechRecognition?: SpeechRecognitionCtor
	}
	return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Whether this browser can dictate at all. Firefox cannot. */
export function isSpeechInputSupported(): boolean {
	return recognitionCtor() !== null
}

/** A sentence needs a space after the one before it, and only one. */
export function joinDictation(existing: string, spoken: string): string {
	const addition = spoken.trim()
	if (!addition) return existing
	if (!existing) return addition
	return /\s$/.test(existing)
		? `${existing}${addition}`
		: `${existing} ${addition}`
}

export type SpeechInputError =
	| "denied"
	| "no-speech"
	| "failed"
	| "insecure-origin"

/**
 * The recogniser refuses to start outside a secure context, and Chrome answers
 * with the same opaque "not-allowed" it uses for a denied microphone. Dev over
 * a LAN address is the case that hits this.
 */
export function isSecureEnoughForSpeech(): boolean {
	if (typeof window === "undefined") return false
	return window.isSecureContext
}

function toError(code: string): SpeechInputError {
	if (code === "not-allowed" || code === "service-not-allowed") return "denied"
	if (code === "no-speech") return "no-speech"
	return "failed"
}

/**
 * Dictation into any composer, through the browser's own recogniser. No audio
 * leaves the page and nothing is billed, which is why this beats shipping a
 * recording to a transcription endpoint for a button people press in passing.
 */
export function useSpeechInput({
	onTranscript,
	onError,
	language,
}: {
	/** Called with each finished phrase, as it lands. */
	onTranscript: (text: string) => void
	onError?: (error: SpeechInputError) => void
	/** BCP-47 tag. Defaults to the browser's own language. */
	language?: string
}) {
	const [listening, setListening] = useState(false)
	const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
	const onTranscriptRef = useRef(onTranscript)
	const onErrorRef = useRef(onError)
	onTranscriptRef.current = onTranscript
	onErrorRef.current = onError

	const stop = useCallback(() => {
		recognitionRef.current?.stop()
	}, [])

	// For a send: stop() would still deliver the pending phrase into the emptied composer.
	const cancel = useCallback(() => {
		const recognition = recognitionRef.current
		if (!recognition) return
		recognitionRef.current = null
		setListening(false)
		recognition.abort()
	}, [])

	const start = useCallback(() => {
		const Ctor = recognitionCtor()
		if (!Ctor) {
			onErrorRef.current?.("failed")
			return
		}
		if (!isSecureEnoughForSpeech()) {
			onErrorRef.current?.("insecure-origin")
			return
		}
		if (recognitionRef.current) return

		const recognition = new Ctor()
		recognition.lang =
			language ??
			(typeof navigator !== "undefined" ? navigator.language : "en-US")
		recognition.continuous = true
		recognition.interimResults = false

		recognition.onresult = (event) => {
			if (recognitionRef.current !== recognition) return
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i]
				if (!result?.isFinal) continue
				const text = result[0].transcript
				if (text.trim()) onTranscriptRef.current(text)
			}
		}
		recognition.onerror = (event) => {
			// Silence is not a failure worth reporting; the user simply paused.
			if (event.error !== "aborted" && event.error !== "no-speech") {
				onErrorRef.current?.(toError(event.error))
			}
		}
		recognition.onend = () => {
			if (recognitionRef.current !== recognition) return
			recognitionRef.current = null
			setListening(false)
		}

		recognitionRef.current = recognition
		setListening(true)
		try {
			recognition.start()
		} catch {
			recognitionRef.current = null
			setListening(false)
			onErrorRef.current?.("failed")
		}
	}, [language])

	const toggle = useCallback(() => {
		if (listening) stop()
		else start()
	}, [listening, start, stop])

	useEffect(() => () => recognitionRef.current?.abort(), [])

	return {
		listening,
		start,
		stop,
		cancel,
		toggle,
		supported: isSpeechInputSupported(),
	}
}

export type SpeechInput = ReturnType<typeof useSpeechInput>
